import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Clock, Loader2 } from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  "Medicines Optimisation": "#0F382C",
  "ARRS & Workforce": "#0D9488",
  "Prescribing Safety": "#7A1F2B",
  "Primary Care": "#C5A059",
};

const CATEGORY_HEADINGS = {
  "Medicines Optimisation": "Medicines optimisation and reviews.",
  "ARRS & Workforce": "ARRS, funding and workforce.",
  "Prescribing Safety": "Prescribing safety and high-risk medicines.",
  "Primary Care": "Primary care operations.",
};

const CATEGORY_ORDER = Object.keys(CATEGORY_HEADINGS);

const ArticleCard = ({ a, delay = 0, index = 0 }) => {
  const color = CATEGORY_COLORS[a.category] || "#0F382C";
  return (
    <Reveal delay={delay}>
      <Link
        to={`/insights/${a.slug}`}
        data-testid={`insight-card-${a.slug}`}
        className="group flex flex-col h-full bg-white border border-[#0B192C]/10 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_-18px_rgba(11,25,44,0.18)] hover:border-[#0B192C]/25"
      >
        <div className="w-8 h-px" style={{ backgroundColor: color }} />
        <p className="mt-5 flex items-center justify-between gap-2">
          <span className="font-mono-brand text-[10px] uppercase tracking-[0.16em]" style={{ color }}>
            {a.category}
          </span>
          <span className="font-mono-brand text-[10px] tracking-[0.16em] text-[#94A3B8]">
            {String(index + 1).padStart(2, "0")}
          </span>
        </p>
        <h3 className="mt-4 font-display text-2xl font-semibold text-[#0B192C] leading-snug group-hover:text-[#0F382C] transition-colors duration-300">
          {a.title}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-[#475569] flex-1">{a.excerpt}</p>
        <div className="mt-6 pt-5 border-t border-[#0B192C]/10 flex items-center justify-between gap-3">
          <span className="flex items-center gap-2 text-xs text-[#94A3B8]">
            <Clock size={12} /> {a.read_time} min
            <span aria-hidden="true">·</span>
            {new Date(a.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
          </span>
          <ArrowUpRight size={16} className="text-[#94A3B8] transition-all duration-300 group-hover:text-[#C5A059] group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </Link>
    </Reveal>
  );
};

const Insights = () => {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    axios.get(`${API}/insights`).then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  const categories = articles
    ? CATEGORY_ORDER.filter((c) => articles.some((a) => a.category === c)).concat(
        [...new Set(articles.map((a) => a.category))].filter((c) => !CATEGORY_ORDER.includes(c))
      )
    : [];

  return (
    <main data-testid="insights-page">
      <Seo
        title="Insights — Analysis & Guidance for Primary Care | NCS"
        description="Practical, current articles from the NCS team — written for the people running medicines management, clinical services and patient transport today."
      />

      {/* EDITORIAL HERO */}
      <section className="relative overflow-hidden pt-36 sm:pt-44 pb-14 sm:pb-20 border-b border-[#0B192C]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <p className="font-mono-brand text-[11px] uppercase tracking-[0.24em] text-[#0F382C]">Insights</p>
          <h1
            data-testid="insights-heading"
            className="mt-5 font-display font-medium tracking-tight leading-[1.08] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl max-w-4xl"
          >
            <MaskedLine delay={0.25}>Analysis and guidance for</MaskedLine>
            <MaskedLine delay={0.4}><span className="font-serif-accent">primary care leaders.</span></MaskedLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#475569] max-w-2xl">
              Practical, current articles from the NCS team — written for the people actually
              running medicines management, clinical services and patient transport today.
            </p>
          </Reveal>
        </div>
      </section>

      {/* LATEST */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono-brand text-[11px] uppercase tracking-[0.22em] text-[#C5A059]">From the NCS team</p>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B192C]">
                Latest analysis
              </h2>
            </div>
          </Reveal>

          {!articles ? (
            <Loader2 size={26} className="animate-spin text-[#0F382C] mx-auto mt-16" />
          ) : articles.length === 0 ? (
            <p className="text-sm text-[#64748B] py-16 text-center" data-testid="insights-empty">
              Articles are on their way — check back soon.
            </p>
          ) : (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="insights-latest">
              {articles.slice(0, 3).map((a, i) => (
                <ArticleCard key={a.slug} a={a} delay={(i % 3) * 0.08} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CATEGORY SECTIONS */}
      {articles && categories.map((cat) => (
        <section key={cat} className="py-14 sm:py-20 border-t border-[#0B192C]/10" data-testid={`insights-section-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="font-mono-brand text-[11px] uppercase tracking-[0.22em]" style={{ color: CATEGORY_COLORS[cat] || "#0F382C" }}>{cat}</p>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B192C] max-w-3xl">
                {CATEGORY_HEADINGS[cat] || `${cat}.`}
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {articles.filter((a) => a.category === cat).map((a, i) => (
                <ArticleCard key={a.slug} a={a} delay={(i % 3) * 0.08} index={i} />
              ))}
            </div>
          </div>
        </section>
      ))}
    </main>
  );
};

export default Insights;
